// Construcció de les opcions ApexCharts d'un widget (merge dels params amb defaults +
// formatters + dimming de cross-filter/interactiu + tooltip d'agregades + truncat de
// llegenda). Extret de Dashboard.getMergedOptions. Funció pura: rep el widget JA
// efectiu, el node de dades i l'estat d'interacció per arguments (abans venien de refs
// de la instància de Dashboard). El preview de l'editor passa estat d'interacció buit.

import { tConfig } from '../i18n/index.js';
import { FORMATTER_PRESETS } from '../utils/widgetTransform.js';

/**
 * @param {object} widget widget JA efectiu (getEffectiveWidget aplicat pel cridador)
 * @param {object|undefined} dataNode node de dades transformat (chartData[dataSourceKey])
 * @param {object} ctx
 * @param {string} [ctx.lang]
 * @param {object} [ctx.crossFilters] mapa { dataSourceKey: { field: value } }
 * @param {object} [ctx.globalFilters] mapa { field: value } del mode interactiu
 * @param {boolean} [ctx.interactive] la pantalla està en mode interactiu
 * @param {number} [ctx.screenLegendMaxChars] legendMaxChars de la pantalla (fallback)
 * @param {object} [ctx.metaLabels] labels i18n resolts pels serveis, per clau neta
 * @return {object} opcions ApexCharts
 */
export function buildMergedOptions(widget, dataNode, ctx = {}) {
  const {
    lang = 'ca_es',
    crossFilters = {},
    globalFilters = {},
    interactive = false,
    screenLegendMaxChars,
    metaLabels = {}
  } = ctx;

  const merged = JSON.parse(JSON.stringify(widget.params || {}));

  const resolveFormatter = (f) => {
    if (typeof f !== 'string') return f;
    return FORMATTER_PRESETS[f] ?? undefined;
  };
  if (merged.xaxis?.labels) merged.xaxis.labels.formatter = resolveFormatter(merged.xaxis.labels.formatter);
  if (merged.yaxis?.labels) merged.yaxis.labels.formatter = resolveFormatter(merged.yaxis.labels.formatter);

  // Títol del widget: forma canònica = params.title multilang pla; també s'accepta la
  // forma antiga { text: multilang }. ApexCharts l'espera com { text: '<resolt>' }.
  const rawTitle = (merged.title && typeof merged.title === 'object' && merged.title.text !== undefined)
    ? merged.title.text
    : merged.title;
  if (rawTitle !== undefined && rawTitle !== null && rawTitle !== '') {
    merged.title = { text: tConfig(rawTitle, lang) };
  }
  if (merged.yaxis?.title?.text) merged.yaxis.title.text = tConfig(merged.yaxis.title.text, lang);
  if (merged.xaxis?.title?.text) merged.xaxis.title.text = tConfig(merged.xaxis.title.text, lang);

  if (dataNode) {
    // Enunciats de domini: es mapegen NOMÉS a la còpia de render (merged); dataNode
    // (categories/labels) es queda amb la KEY crua perquè clics, dimming, cross-filter i
    // KPIs segueixin machejant per key. Valors no-domini passen tal qual.
    const dl = dataNode.domainLabels || {};
    const mapDom = (v) => (v != null && dl[v] != null) ? dl[v] : v;
    if (dataNode.categories) {
      if (!merged.xaxis) merged.xaxis = {};
      merged.xaxis.categories = dataNode.categories.map(mapDom);
    }
    if (dataNode.labels) {
      merged.labels = dataNode.labels.map(mapDom);
    }
  }

  const isBarType = merged.chart?.type === 'bar';
  const cf = widget.params?.crossfilter;
  if (cf) {
    const activeValue = crossFilters[cf.target]?.[cf.field];
    if (activeValue !== undefined) {
      const categories = dataNode?.categories || dataNode?.labels || [];
      const baseColor = (widget.params.colors && widget.params.colors[0]) || '#0b5394';
      const dimColor = '#d1d5db';
      merged.colors = categories.map(c => String(c) === String(activeValue) ? baseColor : dimColor);
      if (isBarType) {
        if (!merged.plotOptions) merged.plotOptions = {};
        if (!merged.plotOptions.bar) merged.plotOptions.bar = {};
        merged.plotOptions.bar.distributed = true;
      }
      if (!merged.legend) merged.legend = {};
      merged.legend.show = false;
    }
  }

  // Mode interactiu: ressalta el valor seleccionat al gràfic font (la seva pròpia
  // dimensió) i atenua la resta.
  if (interactive) {
    const dimParam = widget.params?.dimension;
    const selField = Array.isArray(dimParam) ? dimParam[0] : dimParam;
    const selValue = selField ? globalFilters[selField] : undefined;
    if (selValue !== undefined) {
      const categories = dataNode?.categories || dataNode?.labels || [];
      const baseColor = (widget.params.colors && widget.params.colors[0]) || '#0b5394';
      const dimColor = '#d1d5db';
      merged.colors = categories.map(c => String(c) === String(selValue) ? baseColor : dimColor);
      if (isBarType) {
        if (!merged.plotOptions) merged.plotOptions = {};
        if (!merged.plotOptions.bar) merged.plotOptions.bar = {};
        merged.plotOptions.bar.distributed = true;
      }
    }
  }

  const numberFormatter = {
    formatter: (val) => {
      if (val === undefined || val === null || isNaN(val)) return val;
      if (val % 1 !== 0) {
        return Number(val).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      }
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    }
  };

  // Per gràfics pie/donut, el tooltip NO ha d'afegir % (són números raw, no percentatges)
  const tooltipFormatter = {
    formatter: (val) => {
      if (val === undefined || val === null || isNaN(val)) return val;
      if (val % 1 !== 0) {
        return Number(val).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      }
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    }
  };

  // euro: import en € sense decimals (arrodonit). S'aplica a dataLabels, eixos de valor,
  // tooltip i tooltip d'agregades. Els números de categoria (eix de text) tornen tal qual.
  if (widget.params?.euro) {
    const euroFmt = (val) => {
      if (val === undefined || val === null || isNaN(val)) return val;
      return Math.round(Number(val)).toLocaleString('es-ES', { maximumFractionDigits: 0, useGrouping: 'always' }) + ' €';
    };
    numberFormatter.formatter = euroFmt;
    tooltipFormatter.formatter = euroFmt;
  }

  // Pie/donut: les dataLabels són percentatges → afegim el símbol "%".
  const isPie = merged.chart?.type === 'donut' || merged.chart?.type === 'pie';
  // Pie/donut: llegenda sempre a sota, perquè l'àrea del gràfic no variï segons l'amplada
  // del text de la llegenda (evita mides molt diferents entre gràfics).
  if (isPie) {
    if (!merged.legend) merged.legend = {};
    merged.legend.position = 'bottom';
  }
  const percentFormatter = (val) => {
    if (val === undefined || val === null || isNaN(val)) return val;
    return Number(val).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) + '%';
  };
  const dataLabelFormatter = isPie ? percentFormatter : numberFormatter.formatter;

  if (!merged.dataLabels) merged.dataLabels = { formatter: dataLabelFormatter, enabled: true };
  else if (!merged.dataLabels.formatter) merged.dataLabels.formatter = dataLabelFormatter;

  if (!merged.yaxis) merged.yaxis = { labels: { formatter: numberFormatter.formatter } };
  else if (!merged.yaxis.labels) merged.yaxis.labels = { formatter: numberFormatter.formatter };
  else if (!merged.yaxis.labels.formatter) merged.yaxis.labels.formatter = numberFormatter.formatter;

  if (!merged.xaxis) merged.xaxis = { labels: { formatter: numberFormatter.formatter } };
  else if (!merged.xaxis.labels) merged.xaxis.labels = { formatter: numberFormatter.formatter };
  else if (!merged.xaxis.labels.formatter) merged.xaxis.labels.formatter = numberFormatter.formatter;

  if (!merged.tooltip) merged.tooltip = { y: { formatter: tooltipFormatter.formatter } };
  else if (!merged.tooltip.y) merged.tooltip.y = { formatter: tooltipFormatter.formatter };
  else if (!merged.tooltip.y.formatter) merged.tooltip.y.formatter = tooltipFormatter.formatter;

  // Agregacions numèriques al tooltip: si el widget porta metrics i el backend ha retornat
  // els valors per bucket (dataNode.items[i] té claus "FUNC(field)"), el tooltip afegeix una
  // línia per (camp × funció) sota el count.
  const metrics = Array.isArray(widget.params?.metrics) ? widget.params.metrics : null;
  if (metrics && metrics.length && Array.isArray(dataNode?.items)) {
    const items = dataNode.items;
    const isPieChart = merged.chart?.type === 'donut' || merged.chart?.type === 'pie';
    const fmt = tooltipFormatter.formatter;
    const funcLabels = { SUM: 'Suma', AVG: 'Media', MIN: 'Mínimo', MAX: 'Máximo', MEDIAN: 'Mediana' };
    const labelByKey = metaLabels || {};
    if (!merged.tooltip) merged.tooltip = {};
    merged.tooltip.custom = ({ series, seriesIndex, dataPointIndex, w }) => {
      const idx = isPieChart ? seriesIndex : dataPointIndex;
      const item = items[idx] || {};
      const label = (w.globals.labels && w.globals.labels[idx] != null) ? w.globals.labels[idx] : '';
      const seriesName = (w.globals.seriesNames && w.globals.seriesNames[seriesIndex]) || 'Total';
      const countVal = isPieChart
        ? series[seriesIndex]
        : (Array.isArray(series[seriesIndex]) ? series[seriesIndex][dataPointIndex] : undefined);
      let rows = '';
      for (const m of metrics) {
        if (!m || !m.field || !Array.isArray(m.funcs)) continue;
        const leaf = String(m.field).split('.').pop();
        for (const func of m.funcs) {
          const expr = `${func}(${m.field})`;
          const v = item[expr];
          const cleanKey = expr.replace(/[\[\]" ]/g, '').toLowerCase();
          const name = labelByKey[cleanKey] || ((funcLabels[func] || func) + ' ' + leaf);
          rows += '<div style="display:flex;justify-content:space-between;gap:18px;padding:1px 0;">'
            + '<span style="color:#6b7280;">' + name + '</span>'
            + '<b>' + (v === undefined || v === null ? '—' : fmt(v)) + '</b></div>';
        }
      }
      return '<div style="padding:8px 10px;font-size:12px;min-width:170px;line-height:1.5;">'
        + '<div style="font-weight:700;margin-bottom:4px;">' + label + '</div>'
        + '<div style="display:flex;justify-content:space-between;gap:18px;">'
        + '<span>' + seriesName + '</span><b>' + fmt(countVal) + '</b></div>'
        + '<div style="border-top:1px solid #e5e7eb;margin:5px 0;"></div>'
        + rows
        + '</div>';
    };
  }

  // Retall del text a N caràcters (param del widget o de la pantalla: legendMaxChars).
  const legendMax = widget.params?.legendMaxChars ?? screenLegendMaxChars;
  if (legendMax) {
    const truncate = (s) => {
      const t = String(s ?? '');
      return t.length > legendMax ? t.slice(0, legendMax) + '…' : t;
    };
    if (!merged.legend) merged.legend = {};
    merged.legend.formatter = (seriesName) => truncate(seriesName);
    if ((merged.chart?.type === 'donut' || merged.chart?.type === 'pie') && Array.isArray(merged.labels)) {
      merged.labels = merged.labels.map(truncate);
    }
  }

  return merged;
}
